import { orderBy } from "firebase/firestore";
import { useEffect, useRef, useState } from "react";
import { Accordion, Button, Card, ListGroup, Modal } from "react-bootstrap";
import { FaChevronRight, FaGlobe, FaTrash } from "react-icons/fa";
import { TbSettingsExclamation } from "react-icons/tb";
import { JSONTree } from "react-json-tree";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";

import { getFirebaseConsoleUrl } from "../../api/firestoreBaseApi.jsx";
import {
    LocationDocument,
    TaskDocument,
    useAppCollection,
    useLocationDocument,
    useTaskCollection,
} from "../../api/firestoreWrappers.generated.jsx";
import AppListItem from "../../components/AppListItem/index.jsx";
import AppSettingsTree from "../../components/AppSettings/AppSettingsTree.jsx";
import { Breadcrumbs, breadcrumbPaths } from "../../components/Breadcrumbs";
import Layout, { Title } from "../../components/Layout/Layout";
import urls from "../../urls.jsx";
import { deepMerge } from "../../utils/objectUtils.js";
import { naturalSort } from "../../utils/sortingUtils.js";

export default function LocationAppListPage() {
    const { organizationId, locationId } = useParams();
    const navigate = useNavigate();
    const [apps] = useAppCollection([orderBy("name", "asc")]);
    const [location] = useLocationDocument(organizationId, locationId);
    const [allTasks] = useTaskCollection(organizationId, locationId);
    const tasks = allTasks.filter((task) => task.deleted !== true);
    const [showUninstallModal, setShowUninstallModal] = useState(false);
    const [isUninstalling, setIsUninstalling] = useState(false);
    const [appIdToUninstall, setAppIdToUninstall] = useState(null);
    const [searchParams, setSearchParams] = useSearchParams();
    const itemRefs = useRef(new Map());
    const selectedAppId = searchParams.get("appId");
    const appNames = Object.fromEntries(apps.map((app) => [app.id, app.name]));

    const installedAppIds = location?.apps
        ? location.apps.map((app) => app.id)
        : [];
    const tasksByInstalledAppId = installedAppIds.reduce((acc, appId) => {
        acc[appId] = tasks.filter((task) =>
            task.apps?.some((app) => app.id === appId),
        );
        return acc;
    }, {});

    const uninstallTasks = tasksByInstalledAppId?.[appIdToUninstall] ?? [];
    const removedTasks = uninstallTasks
        .filter((t) => (t?.apps ?? []).length === 1)
        .sort((a, b) => naturalSort(a.name, b.name));
    const modifiedTasks = uninstallTasks
        .filter((t) => (t?.apps ?? []).length > 1)
        .sort((a, b) => naturalSort(a.name, b.name));

    const isPartiallyInViewport = (el) => {
        const rect = el.getBoundingClientRect();
        const viewHeight =
            window.innerHeight || document.documentElement.clientHeight;

        return rect.top < viewHeight && rect.bottom > 0;
    };

    const registerScrollItemRef = (appId) => (el) => {
        if (!el) return;
        itemRefs.current.set(appId, el);

        if (appId === selectedAppId) {
            setTimeout(() => {
                if (!isPartiallyInViewport(el)) {
                    el.scrollIntoView({ block: "start" });
                }
            }, 400);
        }
    };

    useEffect(() => {
        if (!locationId || locationId === "undefined") {
            navigate(urls.locations(organizationId), { replace: true });
        }
    }, [locationId, organizationId, navigate]);

    function installApp(appId) {
        return LocationDocument(organizationId, locationId).transactionUpdate(
            (locationDoc) => {
                const apps = locationDoc?.apps ?? [];

                if (apps.some((app) => app.id === appId)) {
                    return {};
                }

                return {
                    apps: [...apps, { id: appId }],
                };
            },
        );
    }

    async function uninstallApp() {
        if (!appIdToUninstall) return;

        setIsUninstalling(true);

        try {
            await Promise.all([
                ...removedTasks.map((task) =>
                    TaskDocument(organizationId, locationId, task.id).delete(),
                ),
                ...modifiedTasks.map((task) =>
                    TaskDocument(
                        organizationId,
                        locationId,
                        task.id,
                    ).transactionUpdate((taskDoc) => {
                        if (!taskDoc?.apps) {
                            return {};
                        }

                        return {
                            apps: taskDoc.apps.filter(
                                (app) => app.id !== appIdToUninstall,
                            ),
                        };
                    }),
                ),
            ]);

            await LocationDocument(
                organizationId,
                locationId,
            ).transactionUpdate((locationDoc) => {
                if (!locationDoc?.apps) {
                    return {};
                }

                return {
                    apps: locationDoc.apps.filter(
                        (app) => app.id !== appIdToUninstall,
                    ),
                };
            });

            setAppIdToUninstall(null);
            setShowUninstallModal(false);
        } catch (error) {
            // Task delete or task update failed → location doc is NOT modified
            console.error("Uninstall failed:", error);
        }

        setIsUninstalling(false);
    }

    const installedApps = apps
        .filter((app) => installedAppIds.includes(app.id))
        .sort((a, b) => a.name.localeCompare(b.name));
    const availableApps = apps
        .filter((app) => !installedAppIds.includes(app.id))
        .sort((a, b) => a.name.localeCompare(b.name));

    return (
        <Layout>
            <title>Apps | MyCorvus</title>
            <Breadcrumbs
                path={breadcrumbPaths.locationApps}
                location={location}
            />
            <Card className="mb-3">
                <Title title="Apps" />
            </Card>

            <Modal
                show={showUninstallModal}
                onHide={() => {
                    if (isUninstalling) return;
                    setAppIdToUninstall(null);
                    setShowUninstallModal(false);
                }}
                backdrop={isUninstalling ? "static" : true}
                keyboard={!isUninstalling}
            >
                <Modal.Header closeButton>
                    <Modal.Title>Uninstall app</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>
                        {isUninstalling ? (
                            <span>
                                Uninstalling app{" "}
                                <strong>{appNames[appIdToUninstall]}</strong>...
                            </span>
                        ) : (
                            <span>
                                Are you sure you want to uninstall app{" "}
                                <strong>{appNames[appIdToUninstall]}</strong>?
                            </span>
                        )}
                    </p>

                    {removedTasks.length > 0 && (
                        <>
                            <p className="mb-0">
                                {isUninstalling ? (
                                    <span>Deleting tasks:</span>
                                ) : (
                                    <span>
                                        The following task
                                        {removedTasks.length !== 1 && "s"} will
                                        be <strong>deleted</strong>:
                                    </span>
                                )}
                            </p>
                            <ul>
                                {removedTasks.map((task) => (
                                    <li key={task.id}>{task.name}</li>
                                ))}
                            </ul>
                        </>
                    )}

                    {modifiedTasks.length > 0 && (
                        <>
                            <p className="mt-3 mb-0">
                                {isUninstalling ? (
                                    <span>Removing app from tasks:</span>
                                ) : (
                                    <span>
                                        The app will be removed from the
                                        following task
                                        {modifiedTasks.length !== 1 && "s"}:
                                    </span>
                                )}
                            </p>
                            <ul>
                                {modifiedTasks.map((task) => (
                                    <li key={task.id}>{task.name}</li>
                                ))}
                            </ul>
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="outline-secondary"
                        onClick={() => {
                            setAppIdToUninstall(null);
                            setShowUninstallModal(false);
                        }}
                        disabled={isUninstalling}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="danger"
                        onClick={uninstallApp}
                        disabled={isUninstalling}
                    >
                        <FaTrash
                            className="me-2"
                            style={{ marginTop: "-2px" }}
                        />
                        {isUninstalling ? "Uninstalling..." : "Uninstall"}
                    </Button>
                </Modal.Footer>
            </Modal>

            <Card className="mb-3">
                <Card.Header className="px-4 d-flex justify-content-between align-items-start">
                    <div>
                        <b>Installed apps</b>
                    </div>
                    <div className="text-muted">
                        <small>{installedApps.length} apps</small>
                    </div>
                </Card.Header>
                <Accordion
                    activeKey={selectedAppId}
                    onSelect={(key) => {
                        if (key) {
                            setSearchParams({ appId: key }, { replace: true });
                        } else {
                            setSearchParams({}, { replace: true });
                        }
                    }}
                >
                    {installedApps.map((app) => {
                        const locationDocumentPath = LocationDocument(
                            organizationId,
                            locationId,
                        ).path;
                        const appTasks = (
                            tasksByInstalledAppId?.[app.id] ?? []
                        ).sort((a, b) => naturalSort(a.name, b.name));
                        const locationAppSettings =
                            location?.apps?.find(
                                (locationApp) => locationApp.id === app.id,
                            )?.settings ?? {};
                        const hasCustomSettings =
                            Object.keys(locationAppSettings).length > 0;
                        const appSettings = deepMerge(
                            app.settings,
                            locationAppSettings,
                        );

                        return (
                            <Accordion.Item
                                key={app?.id}
                                eventKey={app.id}
                                ref={registerScrollItemRef(app.id)}
                            >
                                <Accordion.Header>
                                    <AppListItem
                                        app={app}
                                        onUninstallApp={() => {
                                            setAppIdToUninstall(app?.id);
                                            setShowUninstallModal(true);
                                        }}
                                        hasCustomSettings={hasCustomSettings}
                                        badgeText={
                                            <>
                                                {appTasks.length}{" "}
                                                {appTasks.length === 1
                                                    ? "task"
                                                    : "tasks"}
                                            </>
                                        }
                                        appSettingsUrl={urls.locationAppSettings(
                                            organizationId,
                                            locationId,
                                            app.id,
                                        )}
                                    />
                                </Accordion.Header>
                                <Accordion.Body>
                                    <div className="d-flex gap-2 mb-3">
                                        <Button
                                            size="sm"
                                            variant="outline-secondary"
                                            onClick={() =>
                                                navigate(
                                                    urls.allApps() +
                                                        `?appId=${app.id}`,
                                                )
                                            }
                                        >
                                            <FaGlobe
                                                className="me-2"
                                                style={{ marginTop: "-2px" }}
                                            />
                                            Global app settings
                                        </Button>
                                    </div>
                                    <AppSettingsTree
                                        resolvedSettings={appSettings}
                                        overrideSettings={locationAppSettings}
                                        url={getFirebaseConsoleUrl(
                                            locationDocumentPath,
                                        )}
                                        className="mb-4"
                                    />

                                    {appTasks.length > 0 ? (
                                        <>
                                            <div className="small text-uppercase text-secondary fw-bold border-bottom border-3 mb-1">
                                                Used in tasks:
                                            </div>
                                            <div className="mb-2">
                                                <ListGroup variant="flush">
                                                    {appTasks.map((task) => {
                                                        const taskAppSettings =
                                                            task?.apps?.find(
                                                                (taskApp) =>
                                                                    taskApp.id ===
                                                                    app?.id,
                                                            )?.settings ?? {};
                                                        const hasCustomTaskAppSettings =
                                                            Object.keys(
                                                                taskAppSettings,
                                                            ).length > 0;
                                                        return (
                                                            <>
                                                                <ListGroup.Item
                                                                    key={
                                                                        task.id
                                                                    }
                                                                    action
                                                                    as={Link}
                                                                    to={
                                                                        urls.task(
                                                                            organizationId,
                                                                            locationId,
                                                                            task.id,
                                                                        ) +
                                                                        `?appId=${app.id}`
                                                                    }
                                                                    className={`small text-muted p-0 ${hasCustomTaskAppSettings ? "fw-bold" : ""}`}
                                                                >
                                                                    <div className="d-flex justify-content-between align-items-center">
                                                                        <div>
                                                                            {
                                                                                task.name
                                                                            }
                                                                            {hasCustomTaskAppSettings && (
                                                                                <TbSettingsExclamation
                                                                                    className="ms-1"
                                                                                    size="16px"
                                                                                    style={{
                                                                                        marginTop:
                                                                                            "-4px",
                                                                                    }}
                                                                                />
                                                                            )}
                                                                        </div>
                                                                        <div>
                                                                            <FaChevronRight
                                                                                style={{
                                                                                    color: "#b9b9b9",
                                                                                    marginTop:
                                                                                        "-4px",
                                                                                }}
                                                                            />
                                                                        </div>
                                                                    </div>
                                                                </ListGroup.Item>
                                                                {hasCustomTaskAppSettings && (
                                                                    <div className="mb-3 fw-bold">
                                                                        <JSONTree
                                                                            data={
                                                                                taskAppSettings
                                                                            }
                                                                            hideRoot={
                                                                                true
                                                                            }
                                                                            sortObjectKeys={
                                                                                true
                                                                            }
                                                                            shouldExpandNodeInitially={() =>
                                                                                true
                                                                            }
                                                                            invertTheme={
                                                                                true
                                                                            }
                                                                            theme={{
                                                                                extend: "monokai",
                                                                                tree: {
                                                                                    marginTop:
                                                                                        "0px",
                                                                                    marginBottom:
                                                                                        "0px",
                                                                                    fontSize:
                                                                                        "14px",
                                                                                    lineHeight:
                                                                                        "1",
                                                                                },
                                                                                base0D: "#212529", // Text color
                                                                                base00: "transparent", // Background
                                                                            }}
                                                                        />
                                                                    </div>
                                                                )}
                                                            </>
                                                        );
                                                    })}
                                                </ListGroup>
                                            </div>
                                        </>
                                    ) : (
                                        <div className="small text-uppercase text-secondary fw-bold">
                                            Not used in any task
                                        </div>
                                    )}
                                </Accordion.Body>
                            </Accordion.Item>
                        );
                    })}
                </Accordion>
            </Card>
            <Card className="mb-3">
                <Card.Header className="px-4 d-flex justify-content-between align-items-start">
                    <div>
                        <b>Available apps</b>
                    </div>
                    <div className="text-muted">
                        <small>{availableApps.length} apps</small>
                    </div>
                </Card.Header>
                <ListGroup variant="flush">
                    {availableApps.length === 0 && (
                        <ListGroup.Item
                            className="px-4 py-2 d-flex justify-content-between align-items-center"
                            key={"Available apps"}
                        >
                            No available apps
                        </ListGroup.Item>
                    )}
                    {availableApps.map((app) => (
                        <ListGroup.Item
                            className="px-4 py-2"
                            key={`applist-${app.id}`}
                        >
                            <AppListItem app={app} onInstallApp={installApp} />
                        </ListGroup.Item>
                    ))}
                </ListGroup>
            </Card>
        </Layout>
    );
}
